import areEqual from "fbjs/lib/areEqual";

import { reject } from "../../../../../../Common/TypingUtils";
import { Opaque } from "../../../../Helpers/TypingUtils";
import { IterUtils } from "../../../../../../Common/IterableUtils";
import { CurrentInstanceContext } from "../../../Internals/ControlContexts/PathContexts/PushInstance";
import { AbsoluteModelPath, PathTokens } from "../../../../../../Common/ModelPath/ModelPath";
import { UnitInfo } from "../../../UnitInfo";

export type SectionName = Opaque<string, "SectionName">;

export interface UnitId {
    pageId: string;
    instance?: string;
    parentInstances?: string[];
    instancesStack?: CurrentInstanceContext[];
}

export function unitWithoutInstance(unitId: UnitId): UnitId {
    if (unitId.instance == undefined) {
        return unitId;
    }
    return {
        ...unitId,
        instance: undefined,
    };
}

export function isUnitIdEquals(left: undefined | UnitId, right: undefined | UnitId): boolean {
    if (left == undefined && right == undefined) {
        return true;
    }
    if (left == undefined || right == undefined) {
        return false;
    }
    return (
        left.pageId === right.pageId &&
        (left.instance ?? undefined) === (right.instance ?? undefined) &&
        areEqual(left.parentInstances ?? [], right.parentInstances ?? [])
    );
}

export function sectionNameToUnitId(sectionName: SectionName): UnitId {
    const [id, ...instances] = sectionName.split("-");
    if (instances.length === 0) {
        return {
            pageId: id ?? reject(),
        };
    } else if (instances.length === 1) {
        return {
            pageId: id ?? reject(),
            instance: instances[0] ?? reject(),
        };
    } else {
        return {
            pageId: id ?? reject(),
            parentInstances: instances.slice(0, -1),
            instance: IterUtils.last(instances) ?? reject(),
        };
    }
}

export const extractInstanceFromBindingPath = (
    bindingPath: AbsoluteModelPath,
    unitId: UnitId,
    unitInfos?: UnitInfo[]
): string => {
    const unitIdBindingPath =
        unitInfos?.find(x => x.id === unitId.pageId)?.bindingPath ?? reject(`Invalid unitId. BindingPath not found!`);
    const unitIdBindingPathParts = unitIdBindingPath.getPathPartsAsArray();
    const unitIdBindingPathLength = unitIdBindingPathParts.length;
    const instancePathToken = bindingPath.getPathPartsAsArray()[unitIdBindingPathLength];
    const instance = PathTokens.isInstanceToken(instancePathToken)
        ? instancePathToken
        : reject(`Invalid instance token [${instancePathToken?.toString()}]`);
    return instance;
};

export function getAllUnitIdInstances(unitId: UnitId): string[] {
    return (unitId.parentInstances ?? []).concat(unitId.instance ?? []);
}

export function unitIdToLegacyUnitId(unitId: UnitId): string;
export function unitIdToLegacyUnitId(unitId: UnitId | undefined): string | undefined;
export function unitIdToLegacyUnitId(unitId: UnitId | undefined): string | undefined {
    if (unitId == undefined) {
        return undefined;
    }
    return [unitId.pageId, ...(unitId.parentInstances || []), unitId.instance].filter(x => x != undefined).join("-");
}

export function unitIdWithInstance(unitId: UnitId, instance?: string): UnitId {
    if (unitId.instance) {
        throw new Error(`${unitId.pageId} already has instance`);
    }
    return { ...unitId, instance: instance };
}

export function createUnitId(id: string): UnitId;
export function createUnitId(id?: string): UnitId | undefined;
export function createUnitId(id: string, instance?: string): UnitId;
export function createUnitId(id?: string, instance?: string): UnitId | undefined {
    return id == undefined ? undefined : { pageId: id, instance: instance };
}
